/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useCallback } from 'react';
import { useAppConfig, useDeviceInfo } from '@airasia-rn/ui';
import { useSetting } from '@airasia-rn/account';
import {
  FEATURE_FLAG_HOTEL_HOME_TILE_REDIRECT_ENABLED,
  FEATURE_FLAG__SNAP_HOME_TILE_REDIRECT_ENABLED,
  TILE_REDIRECT_URL_MAP,
  TILE_PRODUCT_IDS,
} from '../constants';

/**
 * Hook that resolves the redirect URL for product tile clicks on the home page.
 *
 * On mobile web, when the feature flag is enabled, Hotels and SNAP tiles
 * redirect to the new search pages. When disabled, they redirect to the
 * old (legacy) search pages.
 *
 * On desktop web or native, the original CMS `redirectionUrl` is used as-is.
 */
export const useHomeTileRedirect = () => {
  const { config } = useAppConfig();
  const { isMobileWidth } = useDeviceInfo();
  const { value: isNewHotelMwebSearchEnabled } =
    useSetting(FEATURE_FLAG_HOTEL_HOME_TILE_REDIRECT_ENABLED) || {};
  const { value: isNewSnapMwebSearchEnabled } =
    useSetting(FEATURE_FLAG__SNAP_HOME_TILE_REDIRECT_ENABLED) || {};

  const resolveRedirectUrl = useCallback(
    (productId: string, defaultUrl: string): string => {
      // Only override on mobile web
      if (!isMobileWidth) return defaultUrl;

      const tileConfig = TILE_REDIRECT_URL_MAP[productId];
      if (!tileConfig) return defaultUrl;
      const featureFlagMap: Record<string, string | undefined> = {
        [TILE_PRODUCT_IDS.HOTELS]: isNewHotelMwebSearchEnabled,
        [TILE_PRODUCT_IDS.SNAP]: isNewSnapMwebSearchEnabled,
      };

      const isEnabled = featureFlagMap[productId];

      // If the product is not configured for this redirection logic, use the default.
      if (typeof isEnabled !== 'string') {
        return defaultUrl;
      }

      const env: 'prod' | 'stg' | 'dev' =
        config.env === 'prd' ? 'prod' : config.env === 'stg' ? 'stg' : 'dev';

      const url =
        isEnabled === '1' ? tileConfig.new?.[env] : tileConfig.old?.[env];

      return url ?? defaultUrl;
    },
    [
      config.env,
      isMobileWidth,
      isNewHotelMwebSearchEnabled,
      isNewSnapMwebSearchEnabled,
    ],
  );

  return { resolveRedirectUrl };
};
